import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function ServicesPage() {
  useEffect(() => {
    // Scroll to section if hash exists
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
          <p className="text-xl md:text-2xl max-w-3xl font-light">
            Comprehensive consulting solutions designed to unlock growth, optimize performance, and drive sustainable success.
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">What We Offer</h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our services are designed to address your most critical business challenges with proven methodologies and expert guidance.
            </p>
          </div>

          <div className="space-y-16">
            {/* Business Strategy */}
            <div id="business-strategy" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-24">
              <div className="order-2 lg:order-1">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/webp/public/paragraph-page-banner/2022-12/whatwedo_commercialpricingstrategy_header.jpg.webp?itok=2GBC2_W0"
                    alt="Business Strategy"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-line-chart-line"></i>
                  <span className="font-semibold">Strategy</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Business Strategy</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Develop comprehensive strategies that align with your vision and drive sustainable competitive advantage. We help you identify opportunities, assess market dynamics, and create actionable roadmaps for success.
                </p>
                <ul className="space-y-3">
                  {[
                    'Strategic planning and execution',
                    'Competitive analysis and positioning',
                    'Business model innovation',
                    'Long-term growth roadmaps'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Market Analysis */}
            <div id="market-analysis" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-24">
              <div>
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-bar-chart-box-line"></i>
                  <span className="font-semibold">Analysis</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Market Analysis</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Gain deep insights into market trends, customer behavior, and competitive landscapes. Our data-driven approach helps you make informed decisions and identify untapped opportunities.
                </p>
                <ul className="space-y-3">
                  {[
                    'Market research and segmentation',
                    'Customer insights and behavior analysis',
                    'Competitive intelligence',
                    'Industry trend forecasting'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/webp/public/taxonomy-capabilities/2022-12/whatwedo_elevate_header.jpg.webp?itok=LRq-lSOW"
                    alt="Market Analysis"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Pricing Strategy */}
            <div id="pricing-strategy" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-24">
              <div className="order-2 lg:order-1">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/webp/public/taxonomy-capabilities/2022-12/whatwedo_tpse_header.jpg.webp?itok=CIQSHTpu"
                    alt="Pricing Strategy"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-price-tag-3-line"></i>
                  <span className="font-semibold">Pricing</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Pricing Strategy</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Optimize your pricing models to maximize revenue while maintaining market competitiveness. We help you understand value perception and implement pricing strategies that drive profitability.
                </p>
                <ul className="space-y-3">
                  {[
                    'Value-based pricing models',
                    'Price optimization and testing',
                    'Revenue management strategies',
                    'Pricing architecture design'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Growth Consulting */}
            <div id="growth-consulting" className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-24">
              <div>
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-rocket-line"></i>
                  <span className="font-semibold">Growth</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Growth Consulting</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Identify and capitalize on growth opportunities through data-driven strategies and innovative approaches. We help you scale your business sustainably and enter new markets successfully.
                </p>
                <ul className="space-y-3">
                  {[
                    'Market expansion strategies',
                    'New product development',
                    'Sales and marketing optimization',
                    'Partnership and M&A advisory'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/webp/public/content-type-custom-page/2022-12/engine-home.jpg.webp?itok=T3wHrRgY"
                    alt="Growth Consulting"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Operational Improvement */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/card_400_x_480/public/content-type-blog/2025-12/sector_trends_-_ind.jpg.webp?itok=td7KET0Y"
                    alt="Operational Improvement"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-settings-3-line"></i>
                  <span className="font-semibold">Operations</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Operational Improvement</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Streamline operations and enhance efficiency to reduce costs and improve performance. We identify bottlenecks, optimize processes, and implement best practices for operational excellence.
                </p>
                <ul className="space-y-3">
                  {[
                    'Process optimization and automation',
                    'Cost reduction initiatives',
                    'Supply chain management',
                    'Performance measurement systems'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Organizational Change */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-team-line"></i>
                  <span className="font-semibold">Change Management</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Organizational Change</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Guide your organization through transformation with proven change management methodologies. We help you navigate complex transitions while maintaining productivity and employee engagement.
                </p>
                <ul className="space-y-3">
                  {[
                    'Change management strategy',
                    'Organizational design and restructuring',
                    'Culture transformation',
                    'Leadership development'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/card_400_x_480/public/content-type-blog/2025-12/sector_trends_-_con.jpg.webp?itok=86d8yFbE"
                    alt="Organizational Change"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-gray-300">
            Let's discuss how our services can help you achieve your business objectives.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
          >
            Schedule a Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
